import {
  CycleLengthStatus,
  CycleVariabilityStatus,
  HormoneLevelStatus,
  HormoneStabilityStatus,
  OvulationMagnitudeStatus,
  OvulationStatus,
  PhaseLengthStatus,
} from '../../../common/model/type/hormone-summary-status';
import {
  SummaryStatusPresentation,
  SummaryStatusTone,
} from '../molecules/OvSummaryStatus';

const INSUFFICIENT_DATA: SummaryStatusPresentation = {
  label: 'Insufficient data',
  description: 'Not enough measurement data is available.',
  tone: 'neutral',
  icon: 'unknown',
};

export type HormonePatternHormone = 'pg' | 'lrE3g';
export type HormonePatternPhase = 'follicular' | 'luteal';

type LevelToneStatus = Exclude<HormoneLevelStatus, 'insufficient_data'>;

const LEVEL_TONE_BY_HORMONE_PHASE_STATUS: Record<
  HormonePatternHormone,
  Record<HormonePatternPhase, Record<LevelToneStatus, SummaryStatusTone>>
> = {
  pg: {
    follicular: {
      low: 'positive',
      expected: 'positive',
      elevated: 'caution',
    },
    luteal: {
      low: 'negative',
      expected: 'positive',
      elevated: 'positive',
    },
  },
  lrE3g: {
    follicular: {
      low: 'caution',
      expected: 'positive',
      elevated: 'caution',
    },
    luteal: {
      low: 'caution',
      expected: 'positive',
      elevated: 'caution',
    },
  },
};

export function getLevelPresentation(
  hormone: HormonePatternHormone,
  phase: HormonePatternPhase,
  status: HormoneLevelStatus
): SummaryStatusPresentation {
  if (status === 'insufficient_data') {
    return INSUFFICIENT_DATA;
  }

  const tone = LEVEL_TONE_BY_HORMONE_PHASE_STATUS[hormone][phase][status];

  switch (status) {
    case 'low':
      return {
        label: 'Low',
        description: 'Below the expected range.',
        tone,
        icon: 'down',
      };
    case 'expected':
      return {
        label: 'Expected',
        description: 'Within the expected range.',
        tone,
        icon: 'check',
      };
    case 'elevated':
      return {
        label: 'Elevated',
        description: 'Above the expected range.',
        tone,
        icon: 'up',
      };
  }
}

export function getStabilityPresentation(
  status: HormoneStabilityStatus
): SummaryStatusPresentation {
  switch (status) {
    case 'expected':
      return {
        label: 'Expected',
        description:
          'At least half of readings were within the expected range.',
        tone: 'positive',
        icon: 'check',
      };
    case 'variable':
      return {
        label: 'Variable',
        description:
          'Fewer than half of readings were within the expected range.',
        tone: 'caution',
        icon: 'warning',
      };
    case 'insufficient_data':
      return INSUFFICIENT_DATA;
  }
}

export function getOvulationPresentation(
  status: OvulationStatus
): SummaryStatusPresentation {
  switch (status) {
    case 'ovulated':
      return {
        label: 'Ovulated',
        description: 'The cycle was classified as ovulatory.',
        tone: 'positive',
        icon: 'check',
      };
    case 'possible_ovulation':
      return {
        label: 'Possible ovulation',
        description: 'An LH surge was detected without a qualifying PdG rise.',
        tone: 'caution',
        icon: 'warning',
      };
    case 'anovulatory':
      return {
        label: 'Anovulatory',
        description: 'The cycle was classified as anovulatory.',
        tone: 'negative',
        icon: 'warning',
      };
    case 'insufficient_data':
      return INSUFFICIENT_DATA;
  }
}

export function getMagnitudePresentation(
  status: OvulationMagnitudeStatus
): SummaryStatusPresentation {
  switch (status) {
    case 'low':
      return {
        label: 'Low',
        description: 'Peak LH was below the patient-adjusted surge threshold.',
        tone: 'negative',
        icon: 'down',
      };
    case 'adequate':
      return {
        label: 'Adequate',
        description: 'Peak LH was within the patient-adjusted adequate range.',
        tone: 'positive',
        icon: 'check',
      };
    case 'elevated':
      return {
        label: 'Elevated',
        description: 'Peak LH was above the patient-adjusted adequate range.',
        tone: 'negative',
        icon: 'up',
      };
    case 'insufficient_data':
      return INSUFFICIENT_DATA;
  }
}

export function getPhaseLengthPresentation(
  status: PhaseLengthStatus,
  expectedRange: string
): SummaryStatusPresentation {
  switch (status) {
    case 'short':
      return {
        label: 'Short',
        description: `Typical range: ${expectedRange}.`,
        tone: 'negative',
        icon: 'down',
      };
    case 'expected':
      return {
        label: 'Expected',
        description: `Typical range: ${expectedRange}.`,
        tone: 'positive',
        icon: 'check',
      };
    case 'long':
      return {
        label: 'Long',
        description: `Typical range: ${expectedRange}.`,
        tone: 'caution',
        icon: 'up',
      };
    case 'insufficient_data':
      return INSUFFICIENT_DATA;
  }
}

export function getCycleLengthPresentation(
  status: CycleLengthStatus
): SummaryStatusPresentation {
  if (status === 'extended') {
    return {
      label: 'Extended cycle',
      description: 'Cycle length was longer than 90 days.',
      tone: 'negative',
      icon: 'up',
    };
  }

  return getPhaseLengthPresentation(status, '22–35 days');
}

export function getVariabilityPresentation(
  status: CycleVariabilityStatus
): SummaryStatusPresentation {
  switch (status) {
    case 'expected':
      return {
        label: 'Expected',
        description: 'Cycle-to-cycle variation was 7 days or less.',
        tone: 'positive',
        icon: 'check',
      };
    case 'variable':
      return {
        label: 'Variable',
        description: 'Cycle-to-cycle variation was more than 7 days.',
        tone: 'caution',
        icon: 'warning',
      };
    case 'insufficient_data':
      return INSUFFICIENT_DATA;
  }
}
